import React, { useEffect, useState } from "react";
import axios from "axios";
import style from "./TwoFactorAuthSettings.module.scss";
import { saveAs } from "file-saver";
import Modal from "react-modal";
import { PageLoader } from "../../Loaders/PageLoader";
import { Loader } from "../../Loaders/Loader";
import { getImageUrl } from "../../../../commons/assets_path";
import { useTranslation } from "react-i18next";

type Props = {
  client: string;
  myAccount: {
    id: number;
    role: string;
  };
};

const twoFactorAuthModalStyles: Modal.Styles = {
  overlay: {
    zIndex: 1501,
    background: "rgba(34,34,34, 0.3)",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 600,
    width: "100%",
    borderRadius: 12,
    padding: 0,
  },
};

const TwoFactorAuthSettings = ({ client, myAccount }: Props) => {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState<string>("");
  const [isOtp, setIsOtp] = useState<boolean>(false);
  const [isRecoveryCodes, setIsRecoveryCodes] = useState<boolean>(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [isForce2FA, setIsForce2FA] = useState<boolean>(false);
  const [otpAttempt, setOtpAttempt] = useState<string>("");
  const [is2FADisableModal, setIs2FADisableModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const get2FAStatus = async () => {
    const response = await axios.get(`/${client}/settings/members/two_factor_auth/edit.json`);
    setQrCode(response.data.qr_code);
    setIsOtp(response.data.is_otp_required_for_login);
    setIsForce2FA(response.data.is_force_two_factor_auth);
    setLoading(false);
  };

  const enable2FA = async () => {
    setBtnLoading(true);
    await axios
      .post(`/${client}/settings/members/two_factor_auth.json`, {
        otp_attempt: otpAttempt,
      })
      .then((res) => {
        setRecoveryCodes(res.data.codes);
        setIsRecoveryCodes(true);
      })
      .catch((err) => {
        setErrorMessage(err.response.data.message);
      });
    setBtnLoading(false);
  };

  const disable2FA = async () => {
    const response = await axios.delete(`/${client}/settings/members/two_factor_auth`);
    if (response.status === 200) {
      setIsRecoveryCodes(false);
      setRecoveryCodes([]);
      get2FAStatus();
      setIs2FADisableModal(false);
    }
  };

  const createRecoveryCodes = async () => {
    setLoading(true);
    const response = await axios.post(`/${client}/settings/members/two_factor_auth/refresh_recovery_code`);
    if (response.status === 200) {
      setRecoveryCodes(response.data?.codes);
      setIsRecoveryCodes(true);
      setLoading(false);
    }
  };

  const downloadRecoveryCodes = async () => {
    const response = await axios.get(`/${client}/settings/members/two_factor_auth/csv`, {
      params: { codes: recoveryCodes },
      responseType: "blob",
    });
    if (response.status === 200) {
      const blob = new Blob([response.data], { type: "text/csv" });
      const filename = response.headers["x-filename"];
      saveAs(blob, filename);
    }
  };

  useEffect(() => {
    get2FAStatus();
  }, []);
  return (
    <>
      {loading ? (
        <PageLoader />
      ) : (
        <>
          <div className={style.container}>
            <div className={style.header}>
              <div className={style.headerInfo}>
                <div className={style.title}>
                  {isRecoveryCodes ? t("TwoFactorAuthSettings.リカバリーコード") : t("TwoFactorAuthSettings.2要素認証")}
                </div>
                <div className={style.description}>
                  {isRecoveryCodes ? (
                    <>
                      {t(
                        "TwoFactorAuthSettings.デバイスの紛失・故障等で2FA認証コードを受け取れない場合、リカバリーコードがアカウントにアクセスする代替の方法となります。",
                      )}
                      <br />
                      {t(
                        "TwoFactorAuthSettings.セキュリティの都合上、リカバリーコード以外でアカウントの回復はできませんので必ず保管してください。",
                      )}
                    </>
                  ) : isOtp ? (
                    <>
                      {t("TwoFactorAuthSettings.このアカウントは2要素認証が有効になっています。")}
                      {isForce2FA && (
                        <>
                          <br />
                          {t(
                            "TwoFactorAuthSettings.このワークスペースは2要素認証が強制されているため、2要素認証を無効化することはできません。",
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {t(
                        "TwoFactorAuthSettings.2要素認証を有効化するには認証用アプリ(Google Authenticator等)でQRコードを読み取り、認証コードを入力してください。",
                      )}
                      <br />
                      {t(
                        "TwoFactorAuthSettings.2要素認証を有効化すると、所属する全てのワークスペースへのログイン時に必ず適用されます。",
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {isRecoveryCodes ? (
              <>
                <div>
                  <button className="btn btn-info" onClick={downloadRecoveryCodes}>
                    {t("TwoFactorAuthSettings.リカバリーコードをダウンロード")}
                  </button>
                </div>
                <div>
                  {recoveryCodes.map((code, index) => (
                    <li key={index}>{code}</li>
                  ))}
                </div>
              </>
            ) : isOtp ? (
              <>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0A6982",
                    fontSize: "12px",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={createRecoveryCodes}
                  data-testid="test-create-recovery-codes-text"
                >
                  {t("TwoFactorAuthSettings.リカバリーコードを再生成する")}
                </div>
                {!isForce2FA && (
                  <div style={{ maxWidth: "160px" }}>
                    <button
                      className="btn btn-info"
                      onClick={() => setIs2FADisableModal(true)}
                      style={{ fontWeight: 600 }}
                      data-testid="test-2fa-disable-button"
                    >
                      {t("TwoFactorAuthSettings.2要素認証を解除")}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div dangerouslySetInnerHTML={{ __html: qrCode }} />
                <div className={style.headerInfo}>
                  <div style={{ fontWeight: "600" }}>{t("TwoFactorAuthSettings.認証コード")}</div>
                  <div className={`${style.search} ${errorMessage ? style.errorInput : ""}`}>
                    <input type="text" style={{ width: "100%" }} onChange={(e) => setOtpAttempt(e.target.value)} />
                  </div>
                  {errorMessage && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <img src={getImageUrl("images/icons/error.svg")} />
                      <div style={{ color: "#FF4958", fontSize: "12px" }}>{errorMessage}</div>
                    </div>
                  )}
                </div>
                <div style={{ maxWidth: "160px" }}>
                  <button
                    className="btn btn-info d-flex align-items-center justify-content-center"
                    onClick={enable2FA}
                    style={{ fontWeight: 600 }}
                    data-testid="twofactor-auth-setting-btn"
                  >
                    {btnLoading ? <Loader /> : t("TwoFactorAuthSettings.2要素認証を設定")}
                  </button>
                </div>
              </>
            )}
          </div>
          <Modal
            isOpen={is2FADisableModal}
            onRequestClose={() => setIs2FADisableModal(false)}
            style={twoFactorAuthModalStyles}
          >
            <div className={style.modal}>
              <div className={style.modalTitle}>{t("TwoFactorAuthSettings.本当に2要素認証を無効化しますか？")}</div>
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setIs2FADisableModal(false);
                  }}
                >
                  {t("TwoFactorAuthSettings.キャンセル")}
                </button>
                <button className="btn btn-info" onClick={disable2FA} data-testid="test-disable-2fa-btn">
                  {t("TwoFactorAuthSettings.無効化する")}
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default TwoFactorAuthSettings;
